import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import { ChevronsUpDown, Check, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIntegrationService } from '@/hooks/use-integration-service';
import type { Project } from '@/types/financials';

// Maximum length for project codes (matches backend MaxLength(100))
const MAX_PROJECT_CODE_LENGTH = 100;

interface ProjectPickerProps {
  readonly value?: string;
  readonly displayValue?: string;
  readonly onValueChange: (code: string, name: string) => void;
  readonly disabled?: boolean;
  readonly placeholder?: string;
  readonly className?: string;
}

export function ProjectPicker({
  value,
  displayValue,
  onValueChange,
  disabled = false,
  placeholder = 'Select or enter project...',
  className,
}: Readonly<ProjectPickerProps>) {
  const { service, isEnabled } = useIntegrationService();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value ?? '');

  // Sync input value with external value changes
  useEffect(() => {
    setInputValue(value ?? '');
  }, [value]);

  useEffect(() => {
    // Only fetch projects if integration is enabled
    if (!isEnabled) {
      setProjects([]);
      return;
    }

    const fetchProjects = async () => {
      setLoading(true);
      setError(false);
      try {
        const data = await service.getProjects();
        setProjects(data);
      } catch (err) {
        console.error('Failed to fetch projects:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [service, isEnabled]);

  const hasIntegrationData = projects.length > 0;

  const handleSelect = (project: Project) => {
    onValueChange(project.code, project.name);
    setInputValue(project.code);
    setOpen(false);
  };

  const handleClear = () => {
    onValueChange('', '');
    setInputValue('');
    setOpen(false);
  };

  const handleInputChange = (newValue: string) => {
    // Enforce max length
    const truncatedValue = newValue.slice(0, MAX_PROJECT_CODE_LENGTH);
    setInputValue(truncatedValue);
    
    // Check if the value matches an existing project
    const matchingProject = projects.find(p => p.code === truncatedValue);
    if (matchingProject) {
      onValueChange(matchingProject.code, matchingProject.name);
    } else {
      // Custom entry - use the value as both code and name
      onValueChange(truncatedValue, truncatedValue);
    }
  };

  const handleInputBlur = () => {
    // Ensure the value is committed on blur
    if (inputValue !== value) {
      const matchingProject = projects.find(p => p.code === inputValue);
      if (matchingProject) {
        onValueChange(matchingProject.code, matchingProject.name);
      } else {
        onValueChange(inputValue, inputValue);
      }
    }
  };

  const getSelectedDisplay = () => {
    if (loading) {
      return (
        <span className="flex items-center">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading...
        </span>
      );
    }
    if (value) {
      const selectedProject = projects.find(p => p.code === value);
      if (selectedProject) return `${selectedProject.code} - ${selectedProject.name}`;
      if (displayValue && displayValue !== value) return `${value} - ${displayValue}`;
      return value;
    }
    return placeholder;
  };

  if (disabled) {
    return (
      <Input
        value={displayValue && displayValue !== value ? `${value} - ${displayValue}` : (value ?? '')}
        readOnly
        className={cn('bg-muted', className)}
      />
    );
  }

  // If no integration data, show a simple input field for custom entry
  if (!hasIntegrationData && !loading) {
    return (
      <div className={className}>
        <Input
          value={inputValue}
          onChange={(e) => handleInputChange(e.target.value)}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          maxLength={MAX_PROJECT_CODE_LENGTH}
          className="w-full"
        />
        {error && (
          <p className="text-sm text-destructive mt-1">Failed to load projects</p>
        )}
      </div>
    );
  }

  return (
    <div>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn("w-full justify-between font-normal bg-white", className)}
            disabled={loading}
          >
            {getSelectedDisplay()}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command>
            <CommandInput 
              placeholder="Search or enter custom value..." 
              value={inputValue}
              onValueChange={(search) => {
                setInputValue(search);
              }}
            />
            <CommandEmpty>
              {error && 'Failed to load projects.'}
              {!error && inputValue && (
                <CommandItem
                  onSelect={() => {
                    const truncatedValue = inputValue.slice(0, MAX_PROJECT_CODE_LENGTH);
                    onValueChange(truncatedValue, truncatedValue);
                    setOpen(false);
                  }}
                  className="cursor-pointer"
                >
                  <Check className="mr-2 h-4 w-4 opacity-0" />
                  Use &quot;{inputValue.slice(0, MAX_PROJECT_CODE_LENGTH)}&quot;
                </CommandItem>
              )}
              {!error && !inputValue && 'Type to enter a custom value'}
            </CommandEmpty>
            <CommandGroup className="max-h-64 overflow-y-auto">
              <CommandItem
                key="none"
                value="none"
                onSelect={handleClear}
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    !value ? "opacity-100" : "opacity-0"
                  )}
                />
                <span className="text-muted-foreground">None</span>
              </CommandItem>
              {projects.map((project) => (
                <CommandItem
                  key={project.code}
                  value={`${project.code} ${project.name}`}
                  onSelect={() => handleSelect(project)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === project.code ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="font-medium">{project.code}</span>
                  <span className="ml-2 text-muted-foreground">{project.name}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
      {error && (
        <p className="text-sm text-destructive mt-1">Failed to load projects</p>
      )}
    </div>
  );
}
